import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Save } from 'lucide-react'
import { Button } from '../../core/index.js'
import type { FacilitiesAdminApi } from '../adminApi.js'
import type { AdminFacility } from '../index.js'
import { TimeZoneSelect } from '../components/TimeZoneSelect.js'

/** The admin calls this screen uses. */
type AdminApi = FacilitiesAdminApi

const INPUT_CLASS = 'w-full border border-marine/25 bg-white px-2.5 py-1.5 text-xs text-ink focus:outline-none focus:border-marine'

interface FacilityRecordEditorProps {
  api: AdminApi
  facility: AdminFacility
  onSaved: (facility: AdminFacility) => void
  onCancel: () => void
}

/**
 * Edits the two things the platform admin may change on a facility - its
 * name and time zone - through the admin path. The address is not here on
 * purpose: only the operator can set it.
 */
export function FacilityRecordEditor({ api, facility, onSaved, onCancel }: FacilityRecordEditorProps) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(facility.name)
  const [timezone, setTimezone] = useState(facility.timezone ?? '')
  const idPrefix = `facility-record-${facility.facilityId}`

  const mutation = useMutation({
    mutationFn: (input: { name?: string; timezone?: string }) => api.updateFacility(facility.clientId, facility.facilityId, input),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'facilities', facility.clientId] })
      onSaved(updated)
    },
  })

  const isDirty = name.trim() !== facility.name || (timezone !== '' && timezone !== (facility.timezone ?? ''))
  const timezoneChanged = facility.timezone !== undefined && timezone !== '' && timezone !== facility.timezone

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    mutation.mutate({
      ...(name.trim() !== facility.name ? { name: name.trim() } : {}),
      ...(timezone !== '' && timezone !== (facility.timezone ?? '') ? { timezone } : {}),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="border border-marine/15 bg-white p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1" htmlFor={`${idPrefix}-name`}>
            Name
          </label>
          <input id={`${idPrefix}-name`} className={INPUT_CLASS} value={name} onChange={(event) => setName(event.target.value)} required autoFocus />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1" htmlFor={`${idPrefix}-timezone`}>
            Time zone
          </label>
          <TimeZoneSelect id={`${idPrefix}-timezone`} label="Time zone" className={INPUT_CLASS} value={timezone} onChange={setTimezone} />
        </div>
      </div>
      {timezoneChanged && (
        <p className="text-[11px] text-coral mt-2">Changing the time zone moves this facility's day and period boundaries. Running scores can change at the next recalculation.</p>
      )}
      <p className="text-[11px] text-ink-mid mt-3">The address is the operator's to set from their Configuration page; it cannot be changed here.</p>
      {mutation.isError && <p className="text-[11px] font-bold text-coral mt-2">{mutation.error.message}</p>}
      <div className="flex items-center gap-3 mt-3">
        <Button icon={Save} size="sm" type="submit" disabled={!isDirty || name.trim() === '' || mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save facility'}
        </Button>
        <button type="button" onClick={onCancel} disabled={mutation.isPending} className="text-xs font-bold text-marine disabled:opacity-60">
          Cancel
        </button>
      </div>
    </form>
  )
}
